import React, { useState } from "react";
import {
  Card,
  Row,
  Col,
  Timeline,
  Tag,
  Modal,
  Drawer,
  Typography,
  Space,
} from "antd";
import {
  CalendarOutlined,
  CheckCircleFilled,
  ClockCircleOutlined,
  DeleteFilled,
} from "@ant-design/icons";
import dayjs from "dayjs";
import "./invoice-details-drawer-content.scss";
import SuccessCheckIcon from "../../../assets/images/check-success-green-icon.svg";
import DecimalFormatter from "../../../common/decimal-converter/decimal-converter";
import { checkIsTurkeyKazak } from "../../../util/constantUtility";
import { useTranslation } from "react-i18next";
import { BrandColors, brandColor } from "../../../constants/brand";

const { Text } = Typography;

const InvoiceDetailsDrawerContent = ({ data, onOrderClick,userData }) => {
  // States to view Uploaded documents
  const { t } = useTranslation();
  const [documentDrawerOpen, setDocumentDrawerOpen] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);  

  // ✅ Format Date (same as your card logic)
  const getOrdinal = (day) => {
    if (day > 3 && day < 21) return "th";
    switch (day % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };

  const formatDate = (date) => {
    if (!date) return "-";
    const d = dayjs(date);
    const day = d.date();
    return `${day}${getOrdinal(day)} ${d.format("MMM YYYY")}`;
  };

  const formatDateTime = (date) => {
    if (!date) return "";
    const d = dayjs(date);
    const day = d.date();
    return `${day}${getOrdinal(day)}, ${d.format("MMM YYYY hh:mm A")}`;
  };

  // ✅ Timeline Mapping
  const timelineItems = data?.statusActivities?.map((activity) => {
    const isActive =
      activity?.status === "Completed" ||
      activity?.steps?.some((s) => s.status === "Completed");

    return {
      dot: isActive ? <CheckCircleFilled /> : <ClockCircleOutlined />,
      color: isActive ? "green" : "gray",
      children: (
        <>
          <div className={`timeline-title ${isActive ? "active" : ""}`}>
            {activity.title}
          </div>

          {/* Steps */}
          {activity?.steps?.map((step, idx) => (
            <div key={idx}>
              <div
                  className={`timeline-sub ${
                    step.status === "Completed" ? "active" : ""
                  }` }
                    style={{
                      color:
                        step.key === "RejectionReason" &&
                        step.status === "Completed"
                          ? "#FF0606"
                          : (step.key === "PartiallyPaid" || step.key === "PartiallyPaidWithHardCopyPOD") ? brandColor(BrandColors.primary, "#f99e00"):"",
                    }}
              >
                {step.title}
              </div>

              {step.timestamp && (
                <div className="timeline-time">
                  {formatDateTime(step.timestamp)}
                </div>
              )}

              {/* Rejection reason */}
              {step.reason && (
                <div className="timeline-sub" style={{ color: "var(--text-danger, red)" }}>
                  {step.reason}
                </div>
              )}

                        {/* payment summary card */}
          {activity.key === "Payment" && step.paymentDetail && (
            <div className="payment-card">
              {step.paymentDetail.paidAmount > 0 && <div className="payment-row">
                <span>{t("INV_UPL_SUB_MODAL_PAID_AMOUNT")}</span>
                <span className="paid">
                  {step.paymentDetail.currency}{" "}
                  {step.paymentDetail.paidAmount?.toLocaleString()}
                </span>
              </div>}

             {step.paymentDetail.creditNoteAmount > 0 && <div className="payment-row">
                <span>{t("INV_UPL_SUB_MODAL_CREDIT_NOTE")}</span>
                <span className="credit-note">
                  {step.paymentDetail.currency}{" "}
                  {step.paymentDetail.creditNoteAmount?.toLocaleString()}
                </span>
              </div>}

              {step.paymentDetail?.balanceDue > 0 && <hr className="payment-divider" />}

              {step.paymentDetail?.balanceDue > 0 && (
                <div className="payment-row balance">
                  <span>{t("INV_UPL_SUB_MODAL_BALANCE_DUE")}</span>
                  <span className="balance-due">
                    {step.paymentDetail.currency}{" "}
                    {step.paymentDetail.balanceDue.toLocaleString()}
                  </span>
                </div>
              )}
            </div>
          )}



            </div>
          ))}

        </>
      ),
    };
  });

  // View Documents Handlers
  const handlePreviewClick = (fileItem) => {
    setPreviewFile(fileItem);
    setIsPreviewModalOpen(true);
  };

  const handleModalCancel = () => {
    setPreviewFile(null);
    setIsPreviewModalOpen(false);
  };

  return (
    <>
      <div className="invoice-details-wrapper">
        <div className="left-wrapper">
        {/* Header Link */}
        <div
          className="view-documents"
          onClick={() => setDocumentDrawerOpen(true)}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M14.1184 4.01072L10.2756 0.167857C10.1684 0.0607143 10.0238 0 9.87199 0H2.28627C1.9702 0 1.71484 0.255357 1.71484 0.571429V15.4286C1.71484 15.7446 1.9702 16 2.28627 16H13.7148C14.0309 16 14.2863 15.7446 14.2863 15.4286V4.41607C14.2863 4.26429 14.2256 4.11786 14.1184 4.01072ZM12.9684 4.67857H9.6077V1.31786L12.9684 4.67857ZM13.0006 14.7143H3.00056V1.28571H8.39342V5.14286C8.39342 5.34177 8.47243 5.53254 8.61309 5.67319C8.75374 5.81384 8.9445 5.89286 9.14342 5.89286H13.0006V14.7143Z"
              fill={brandColor(BrandColors.primary, "#F99E00")}
            />
          </svg>
          {t("INV_UPL_SUB_MODAL_VIEW_DOC")}
        </div>


        {/* Earnings Card */}
        <Card className="earnings-card" bordered={true}>
          <Row justify="space-between" align="middle">
            <div>
              <div>{t("INV_UPL_SUB_MODAL_TOTAL_EARNING")}</div>
              <div className="value highlight">
                {data?.currency}&nbsp; 
                {data?.amount ? <DecimalFormatter amount={data?.amount} country={checkIsTurkeyKazak(userData?.operatingCountry) ? "TRY" : data?.currency} flag={false} isShowDecimal={true} />:"0"}
              </div>
            </div>

            <div className="issue-date">
              {t("INV_UPL_SUB_MODAL_ISSUE_DATE")} : {formatDate(data?.createdAt)}
            </div>
          </Row>
        </Card>

        <div></div>
        <div>
          <div className="heading-label">{t("INV_UPL_SUB_MODAL_INV_ORDER_MSG")}</div>
          {/* Orders List */}
          <div className="order-list">
            {data?.orders?.map((order) => (
              <Card
                key={order.orderNumber}
                className="order-card"
                bordered={false}
              >
                <Row justify="space-between" align="middle">
                  <div className="order-id">{order.orderNumber}</div>
                  <div
                    className="view-details"
                    onClick={(e) => {
                      e.stopPropagation();
                      onOrderClick(order);
                    }}
                  >
                    {t("INV_UPL_SUB_MODAL_VIEW_DETAIL_BTN")} →
                  </div>
                </Row>

                <Row
                  justify="space-between"
                  align="middle"
                  className="order-footer"
                >
                  <div className="date">
                    <CalendarOutlined /> {formatDate(order.moveDate)}
                  </div>

                  <div className="trips">
                    {t("INV_UPL_SUB_MODAL_TOTAL_TRIP")} <b>{order.totalTrips}</b>
                  </div>
                </Row>
              </Card>
            ))}
          </div>
        </div>
        </div>
        {/* Timeline */}
        <div className="right-wrapper">
          <div className="heading-label">{t("INV_UPL_SUB_MODAL_INV_STATUS")}</div>
          {/* Timeline */}
          <Timeline className="invoice-timeline" items={timelineItems} />
        </div>
      </div>

      {/* DRAWER */}
      <Drawer
        className="add-invoice-drawer"
        title="Uploaded Documents"
        placement="right"
        width={420}
        open={documentDrawerOpen}
        onClose={() => setDocumentDrawerOpen(false)}
      >
        <div className="document-list-container">
          {data?.invoiceDocs?.filter((doc) => doc?.documentPath)?.length ? (
            data.invoiceDocs
              .filter((doc) => doc?.documentPath)
              .map((docItem, index) => {
                const fileName =
                  docItem?.fileName?.split("/")?.pop() ||
                  `Document ${index + 1}`;

                const fileURL = docItem?.documentPath || "";

                const thumbURL = docItem?.documentPathThumb || "";

                const isImage = fileName?.match(/\.(jpg|jpeg|png|webp)$/i);

                return (
                  <div
                    className="document-list-container-item"
                    key={index}
                    style={{ marginBottom: "16px" }}
                  >
                    <Text className="doc-title">{fileName}</Text>

                    <Card
                      className="document-card"
                      style={{
                        height: "180px",
                        padding: 0,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        cursor: "pointer",
                        position: "relative",
                      }}
                      onClick={() =>
                        handlePreviewClick({
                          fileURL,
                          fileName,
                        })
                      }
                    >

                      {/* Preview */}
                      <div
                        className="file-preview-wrapper"
                        style={{ cursor: "pointer" }}
                      >
                        {isImage ? (
                          <img
                            src={fileURL}
                            alt={fileName}
                            style={{
                              maxWidth: "100%",
                              maxHeight: "100%",
                            }}
                          />
                        ) : (
                          <img
                            src={thumbURL}
                            alt="preview"
                            style={{
                              width: "60px",
                              height: "60px",
                              objectFit: "contain",
                              margin: "auto",
                            }}
                          />
                        )}
                      </div>
                    </Card>

                    <Space align="center" size={8}>
                      <img
                        src={SuccessCheckIcon}
                        alt="success-check-icon"
                        width={16}
                        height={16}
                        style={{
                          display: "inline-block",
                          verticalAlign: "middle",
                        }}
                      />

                      <Text className="size-text">Uploaded</Text>
                    </Space>
                  </div>
                );
              })
          ) : (
            <div>No uploaded documents</div>
          )}
        </div>
      </Drawer>
      <Modal
        title="Preview"
        open={isPreviewModalOpen}
        onCancel={handleModalCancel}
        centered
        footer={null}
        width="80%"
      >
        {previewFile?.fileURL && (
          <>
            {previewFile.fileName.match(/\.(jpg|jpeg|png|webp)$/i) ? (
              <img
                src={previewFile.fileURL}
                alt={previewFile.fileName}
                style={{
                  width: "100%",
                  maxHeight: "80vh",
                  objectFit: "contain",
                }}
              />
            ) : (
              <iframe
                src={previewFile.fileURL}
                title={previewFile.fileName}
                width="100%"
                height="700px"
              />
            )}
          </>
        )}
      </Modal>
    </>
  );
};

export default InvoiceDetailsDrawerContent;
